import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import ProductCard from '../product/ProductCard';
import Skeleton from '../common/Skeleton';

// Carrossel horizontal de produtos. Usa scroll-snap nativo (sem libs) com
// botões prev/next que rolam por uma "página" do contêiner. Em mobile
// vira swipe-livre (botões somem).
const ProductCarousel = ({
  title,
  description,
  products = [],
  loading = false,
  seeAllHref,
  seeAllLabel = 'Ver todos',
  emptyHint,
}) => {
  const scrollerRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const updateScrollState = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateScrollState();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [products.length, loading]);

  const scroll = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.9, behavior: 'smooth' });
  };

  if (!loading && products.length === 0) {
    if (!emptyHint) return null;
    return (
      <section className="my-6">
        <h2 className="text-xl font-bold text-app-text mb-1">{title}</h2>
        <p className="text-sm text-app-text-secondary">{emptyHint}</p>
      </section>
    );
  }

  return (
    <section className="my-6">
      <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-app-text">{title}</h2>
          {description && <p className="text-sm text-app-text-secondary mt-0.5">{description}</p>}
        </div>
        <div className="flex items-center gap-2">
          {seeAllHref && (
            <Link
              to={seeAllHref}
              className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
            >
              {seeAllLabel} <ArrowRight size={14} />
            </Link>
          )}
          <div className="hidden md:flex gap-1">
            <button
              type="button"
              onClick={() => scroll(-1)}
              disabled={!canPrev}
              className="w-9 h-9 rounded-full border border-app-border bg-white text-app-text disabled:opacity-30 hover:bg-app-surface-hover transition-colors flex items-center justify-center"
              aria-label="Anterior"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => scroll(1)}
              disabled={!canNext}
              className="w-9 h-9 rounded-full border border-app-border bg-white text-app-text disabled:opacity-30 hover:bg-app-surface-hover transition-colors flex items-center justify-center"
              aria-label="Próximo"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 px-4 scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {loading
          ? Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="snap-start shrink-0 w-[260px] sm:w-[280px]">
                <Skeleton variant="block" height={360} />
              </div>
            ))
          : products.map((p) => (
              <div key={p.id} className="snap-start shrink-0 w-[260px] sm:w-[280px]">
                <ProductCard product={p} />
              </div>
            ))}
      </div>
    </section>
  );
};

export default ProductCarousel;
